import { useEffect, useId, useRef, useState } from 'react'
import { SectionNavigationItem } from '@confer/lib'
import { Dropdown } from 'antd'
import styled from 'styled-components'

export interface SectionNavigationProps {
  items: SectionNavigationItem[]
  onNavigate: (id: string) => void
  presentation?: 'banner' | 'compact'
}

const statusLabels = {
  'not-started': 'Not started',
  'in-progress': 'In progress',
  complete: 'Complete',
}

function SectionLabel({ item }: { item: SectionNavigationItem }) {
  const status = item.status ?? 'not-started'
  return (
    <>
      <ProgressIcon viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        {status === 'complete' ? (
          <path d="M2.5 8 6.5 12 13.5 4" />
        ) : (
          <>
            <circle cx="8" cy="8" r="6" />
            {status === 'in-progress' && (
              <path d="M8 2a6 6 0 0 0 0 12Z" fill="currentColor" stroke="none" />
            )}
          </>
        )}
      </ProgressIcon>
      {item.number !== undefined && `${item.number}. `}
      {item.name}
      <AccessibleReason> — {statusLabels[status]}</AccessibleReason>
    </>
  )
}

function sectionTitle(item: SectionNavigationItem) {
  return [statusLabels[item.status ?? 'not-started'], item.disabledReason]
    .filter(Boolean)
    .join(' — ')
}

interface SectionGroup {
  id: string
  name: SectionNavigationItem['name']
  colour?: string
  items: SectionNavigationItem[]
}

function groupSections(items: SectionNavigationItem[]): SectionGroup[] {
  const groups: SectionGroup[] = []
  for (const item of items) {
    const id = item.parent?.id ?? item.id
    let group = groups.find((entry) => entry.id === id)
    if (!group) {
      group = {
        id,
        name: item.parent?.name ?? item.name,
        colour: item.parent?.colour ?? item.colour,
        items: [],
      }
      groups.push(group)
    }
    group.items.push(item)
  }
  return groups
}

function groupStatus(items: SectionNavigationItem[]): NonNullable<SectionNavigationItem['status']> {
  if (items.every((item) => item.status === 'complete')) return 'complete'
  if (items.some((item) => item.status === 'complete' || item.status === 'in-progress'))
    return 'in-progress'
  return 'not-started'
}

export default function SectionNavigation({
  items,
  onNavigate,
  mobile = false,
  presentation,
}: SectionNavigationProps & { mobile?: boolean }) {
  const [open, setOpen] = useState(false)
  const activeButton = useRef<HTMLButtonElement>(null)
  const active = items.find((item) => item.active)
  const activeLabelId = useId()
  const groups = groupSections(items)
  const hierarchical = items.some((item) => item.parent)
  const activeGroupId = active?.parent?.id ?? active?.id
  const [chosenGroupId, setChosenGroupId] = useState<string | undefined>()
  const selectedGroupId = groups.some((group) => group.id === chosenGroupId)
    ? chosenGroupId
    : (activeGroupId ?? groups[0]?.id)
  const selectedGroup = groups.find((group) => group.id === selectedGroupId)
  useEffect(() => {
    activeButton.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [active?.id])
  useEffect(() => {
    setChosenGroupId(activeGroupId)
  }, [activeGroupId])

  if (hierarchical && presentation === 'banner') {
    return (
      <BannerNavigation aria-label="Survey sections">
        <BannerColumn role="group" aria-label="Sections">
          <BannerHeading>Sections</BannerHeading>
          {groups.map((group) => {
            const selected = group.id === selectedGroupId
            const current = group.id === activeGroupId
            const status = groupStatus(group.items)
            return (
              <BannerButton
                key={group.id}
                type="button"
                $active={selected}
                $colour={group.colour}
                aria-pressed={selected}
                title={`${statusLabels[status]}${current ? ' — Current section' : ''}`}
                onClick={() => setChosenGroupId(group.id)}
              >
                <SectionLabel item={{ id: group.id, name: group.name, active: current, status }} />
                {current && <CurrentMarker aria-hidden="true" />}
              </BannerButton>
            )
          })}
        </BannerColumn>
        <BannerColumn role="group" aria-label="Subsections">
          <BannerHeading>Subsections</BannerHeading>
          {selectedGroup?.items.map((item) => (
            <BannerButton
              key={item.id}
              ref={item.active ? activeButton : undefined}
              type="button"
              $active={item.active}
              $colour={item.colour}
              aria-current={item.active ? 'step' : undefined}
              aria-disabled={!!item.disabledReason}
              title={sectionTitle(item)}
              onClick={() => {
                if (!item.disabledReason) onNavigate(item.id)
              }}
            >
              <SectionLabel item={item} />
              {item.disabledReason && <AccessibleReason> — {item.disabledReason}</AccessibleReason>}
            </BannerButton>
          ))}
        </BannerColumn>
      </BannerNavigation>
    )
  }

  if (hierarchical && (presentation === 'compact' || mobile)) {
    const activeGroup = groups.find((group) => group.id === activeGroupId)
    return (
      <CompactNavigation aria-label="Survey sections">
        <Dropdown
          autoFocus
          open={open}
          onOpenChange={setOpen}
          trigger={['click']}
          overlayStyle={{ maxWidth: 'calc(100vw - 2rem)' }}
          menu={{
            style: { maxHeight: '60vh', overflowY: 'auto' },
            selectable: true,
            selectedKeys: active ? [active.id] : [],
            items: groups.map((group) => ({
              type: 'group' as const,
              key: group.id,
              label: group.name,
              children: group.items.map((item) => ({
                key: item.id,
                disabled: !!item.disabledReason,
                title: sectionTitle(item),
                label: (
                  <span aria-current={item.active ? 'step' : undefined}>
                    <SectionLabel item={item} />
                    {item.disabledReason && <Reason>{item.disabledReason}</Reason>}
                  </span>
                ),
              })),
            })),
            onClick: ({ key }) => {
              setOpen(false)
              onNavigate(key)
            },
          }}
        >
          <CompactButton
            type="button"
            aria-label="Choose survey subsection"
            aria-describedby={activeLabelId}
            aria-haspopup="menu"
            aria-expanded={open}
            $active
            $colour={active?.colour}
          >
            <CompactText id={activeLabelId}>
              {activeGroup && <>{activeGroup.name} / </>}
              {active ? <SectionLabel item={active} /> : 'Sections'}
            </CompactText>
            <span aria-hidden="true">▾</span>
          </CompactButton>
        </Dropdown>
      </CompactNavigation>
    )
  }

  if (hierarchical) {
    return (
      <HierarchyNavigation aria-label="Survey sections">
        <Navigation as="div" role="group" aria-label="Sections">
          {groups.map((group) => {
            const selected = group.id === selectedGroupId
            const current = group.id === activeGroupId
            const status = groupStatus(group.items)
            return (
              <GroupButton
                key={group.id}
                type="button"
                $active={selected}
                $colour={group.colour}
                aria-pressed={selected}
                title={`${statusLabels[status]}${current ? ' — Current section' : ''}`}
                onClick={() => setChosenGroupId(group.id)}
              >
                <SectionLabel item={{ id: group.id, name: group.name, active: current, status }} />
                {current && <CurrentMarker aria-hidden="true" />}
              </GroupButton>
            )
          })}
        </Navigation>
        <SubsectionRow role="group" aria-label="Subsections">
          {selectedGroup?.items.map((item) => (
            <SectionButton
              key={item.id}
              ref={item.active ? activeButton : undefined}
              type="button"
              $active={item.active}
              $colour={item.colour}
              aria-current={item.active ? 'step' : undefined}
              aria-disabled={!!item.disabledReason}
              title={sectionTitle(item)}
              onClick={() => {
                if (!item.disabledReason) onNavigate(item.id)
              }}
            >
              <SectionLabel item={item} />
              {item.disabledReason && <AccessibleReason> — {item.disabledReason}</AccessibleReason>}
            </SectionButton>
          ))}
        </SubsectionRow>
      </HierarchyNavigation>
    )
  }

  if (mobile) {
    return (
      <Navigation aria-label="Survey sections">
        <Dropdown
          autoFocus
          open={open}
          onOpenChange={setOpen}
          trigger={['click']}
          overlayStyle={{ maxWidth: 'calc(100vw - 2rem)' }}
          menu={{
            style: { maxHeight: '60vh', overflowY: 'auto' },
            selectable: true,
            selectedKeys: active ? [active.id] : [],
            items: items.map((item) => ({
              key: item.id,
              disabled: !!item.disabledReason,
              title: sectionTitle(item),
              label: (
                <span
                  style={{ whiteSpace: 'normal' }}
                  aria-current={item.active ? 'step' : undefined}
                >
                  <SectionLabel item={item} />
                  {item.disabledReason && <Reason>{item.disabledReason}</Reason>}
                </span>
              ),
            })),
            onClick: ({ key }) => {
              setOpen(false)
              onNavigate(key)
            },
          }}
        >
          <SectionButton
            type="button"
            aria-label="Choose survey section"
            aria-describedby={activeLabelId}
            aria-haspopup="menu"
            aria-expanded={open}
            $active
            $colour={active?.colour}
            title={active && sectionTitle(active)}
          >
            <span id={activeLabelId}>{active ? <SectionLabel item={active} /> : 'Sections'}</span>
            <span aria-hidden="true"> ▾</span>
          </SectionButton>
        </Dropdown>
      </Navigation>
    )
  }

  return (
    <Navigation aria-label="Survey sections">
      {items.map((item) => (
        <SectionButton
          key={item.id}
          ref={item.active ? activeButton : undefined}
          type="button"
          $active={item.active}
          $colour={item.colour}
          aria-current={item.active ? 'step' : undefined}
          aria-disabled={!!item.disabledReason}
          title={sectionTitle(item)}
          onClick={() => {
            if (!item.disabledReason) onNavigate(item.id)
          }}
        >
          <SectionLabel item={item} />
          {item.disabledReason && <AccessibleReason> — {item.disabledReason}</AccessibleReason>}
        </SectionButton>
      ))}
    </Navigation>
  )
}

const ProgressIcon = styled.svg`
  width: 1rem;
  height: 1rem;
  margin-inline-end: 0.4rem;
  vertical-align: -0.15rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
`

const Navigation = styled.nav`
  display: flex;
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  padding: 0.25rem 1rem;
  gap: 0.25rem;
  border-bottom: 1px solid var(--mid-light, #ddd);
`

const HierarchyNavigation = styled.nav`
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
`

const BannerNavigation = styled.nav`
  display: grid;
  grid-template-columns: minmax(0, 42%) minmax(0, 58%);
  width: 100%;
  max-height: calc(100vh - 7.5rem);
  overflow: hidden;
  border: 1px solid rgba(35, 55, 35, 0.18);
  border-radius: 0.5rem;
  background: rgba(250, 252, 250, 0.94);
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.16);
  color: #1d321f;
  backdrop-filter: blur(6px);
  pointer-events: auto;
`

const BannerColumn = styled.div`
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 0.25rem;
  &:first-child {
    border-right: 1px solid rgba(35, 55, 35, 0.15);
  }
`

const BannerHeading = styled.div`
  padding: 0.25rem 0.5rem 0.5rem;
  color: #526554;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`

const CompactNavigation = styled.nav`
  min-width: 0;
  max-width: 100%;
  padding: 0.15rem 1rem;
  border-bottom: 1px solid var(--mid-light, #ddd);
`

const CurrentMarker = styled.span`
  display: inline-block;
  width: 0.35rem;
  height: 0.35rem;
  margin-inline-start: 0.45rem;
  border-radius: 50%;
  background: currentColor;
  vertical-align: middle;
`

const SubsectionRow = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  gap: 0.25rem;
  padding: 0.15rem 1rem 0.15rem 1.5rem;
  background: rgba(0, 0, 0, 0.025);
  border-bottom: 1px solid var(--mid-light, #ddd);
  font-size: 0.92em;
  &::before {
    content: '';
    flex: 0 0 2px;
    align-self: stretch;
    background: var(--mid-light, #ddd);
  }
`

const SectionButton = styled.button<{ $active?: boolean; $colour?: string }>`
  flex: 0 0 auto;
  max-width: 100%;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0.65rem 0.75rem;
  border: 0;
  border-bottom: 3px solid
    ${({ $active, $colour }) => ($active ? ($colour ?? 'currentColor') : 'transparent')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  &[aria-disabled='true'] {
    opacity: 0.55;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -2px;
  }
`

const BannerButton = styled(SectionButton)`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 0.6rem 0.4rem;
  border-bottom: 0;
  border-left: 3px solid
    ${({ $active, $colour }) => ($active ? ($colour ?? 'currentColor') : 'transparent')};
  border-radius: 0.25rem;
  background: ${({ $active }) => ($active ? 'rgba(85, 171, 71, 0.12)' : 'transparent')};
  font-size: 0.84rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
  svg {
    flex: 0 0 1rem;
  }
`

const CompactButton = styled(SectionButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  padding: 0.45rem 0.25rem;
`

const CompactText = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const GroupButton = styled(SectionButton)`
  background: ${({ $active }) => ($active ? 'rgba(0, 0, 0, 0.035)' : 'transparent')};
  position: relative;
`

const Reason = styled.small`
  display: block;
  max-width: min(22rem, 80vw);
  white-space: normal;
`

const AccessibleReason = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
`
